import classnames from 'classnames';
import React, { Component } from 'react';

/**
|---------------------------------------------------------------------
| To prevent the grid from performing the client-side sorting after
| clicking on the header, we are configuring the grid with this
| custom GridHeader component so we can perform our custom sorting.
| (See https://www.ag-grid.com/javascript-grid-header-rendering/)
|----------------------------------------------------------------------
*/

// These constant values are specified per Ag-grid documentation.
const SORT_ASC = 'asc';
const SORT_DESC = 'desc';
const SORT_NONE = 'none';

export class GridCustomHeader extends Component {
	constructor(props) {
		super(props);

		const { column, displayName, enableSorting, enableMenu, dataqa } = props;
		const { colId } = column;
		const sortOrder =
			column.sort === SORT_ASC || column.sort === SORT_DESC
				? column.sort
				: SORT_NONE;

		this.state = {
			sortOrder,
			displayName,
			enableSorting,
			enableMenu,
			dataqa: `${dataqa}-header-col-${colId}`,
		};

		column.addEventListener('sortChanged', this.onSortChanged);
		this.menuButton = null;
	}

	componentWillUnmount() {
		const { column } = this.props;

		column.removeEventListener('sortChanged', this.onSortChanged);
		this.menuButton = null;
	}

	// update state when sort state is changed
	onSortChanged = () => {
		const { column } = this.props;

		if (column.isSortAscending()) {
			this.setState({ sortOrder: SORT_ASC });
		} else if (column.isSortDescending()) {
			this.setState({ sortOrder: SORT_DESC });
		} else {
			this.setState({ sortOrder: SORT_NONE });
		}
	};

	// change the sort state and perform sort by calling grid api - setSort method
	onHeaderClicked = () => {
		const { api, column, enableSorting, onSortChanged, setColumnState } =
			this.props;
		const { sortOrder } = this.state;
		const source = 'uiColumnSorted';

		// perform sort logic if sort feature is enabled
		if (enableSorting) {
			// set sort order state for this column
			switch (sortOrder) {
				case SORT_ASC:
					column.setSort(SORT_DESC, source);
					break;
				case SORT_DESC:
				case SORT_NONE:
				default:
					column.setSort(SORT_ASC, source);
					break;
			}

			// clear out all other columns' sort state
			api.sortController.clearSortBarThisColumn(column, source);

			// call Grid's setColumnState method to save the sort state
			setColumnState();

			// call the parent method to fetch new data based on the new sort state
			if (onSortChanged) {
				onSortChanged(api.getSortModel());
			}
		}
	};

	onMenuClicked = e => {
		const { showColumnMenu } = this.props;
		showColumnMenu(this.menuButton);

		e.stopPropagation();
	};

	renderMenuButton() {
		return (
			<div
				className="ag-icon ag-icon-menu ag-header-cell-button"
				onClick={this.onMenuClicked}
				role="button"
				tabIndex={0}
				ref={menuButton => {
					this.menuButton = menuButton;
				}}
			>
				<i className="fa fa-bars" />
			</div>
		);
	}

	render() {
		const { enableSorting, sortOrder, displayName, enableMenu, dataqa } =
			this.state;
		const sortCellCls = classnames('ag-header-icon', {
			'ag-sort-ascending-icon': sortOrder === SORT_ASC,
			'ag-sort-descending-icon': sortOrder === SORT_DESC,
			'ag-hidden': sortOrder === SORT_NONE,
		});

		return (
			<div
				className={`ag-cell-label-container ag-header-cell-sorted-${sortOrder}`}
				onClick={this.onHeaderClicked}
				role="presentation"
				data-qa={dataqa}
				{...{ dataqa }}
			>
				{enableMenu && this.renderMenuButton()}
				<div className="ag-header-cell-label">
					{enableSorting && (
						<span className={sortCellCls}>
							<span
								className={`ag-icon ag-icon-${sortOrder}`}
								unselectable="on"
							/>
						</span>
					)}
					<span className="ag-header-cell-text">{displayName}</span>
				</div>
			</div>
		);
	}
}
